import { useCallback, useMemo, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import Title from 'components/ui/Title';
import Editorial from 'components/ui/Editorial';
import Form from 'components/form/Form';
import FormFieldset from 'components/form/FormFieldset';
import FormField from 'components/form/FormField';
import SelectInput from 'components/form/SelectInput';
import TextInput from 'components/form/TextInput';
import FormFooter from 'components/form/FormFooter';
import Flex from 'components/grid/Flex';
import Button from 'components/ui/Button';
import Spinner from 'components/ui/Spinner';
import FormError from 'components/form/FormError';
import useMessages from 'components/hooks/useMessages';
import yup from 'components/form/yup';
import useCdmApi from 'components/hooks/useCdmApi';
import { RegistrationRenunciationModel, isRegistrationRenunciationModel } from 'models/candidate';
import DateTimeInput from 'components/form/DateTimeInput';
import { useSelector } from 'react-redux';
import { selectSuperviewer } from 'selectors/candidateSelectors';
import useData from 'components/hooks/useData';
import FormikViolations from 'components/form/FormikViolations';
import { dateFormat, DateFormatPreset, dateParse, ParsableDate } from 'components/utils/DateFormat';
import ChevronRight from 'components/ui/Icon/actions/ChevronRight';
import CheckboxField from 'components/form/CheckboxField';
import useLocale from 'components/hooks/useLocale';
import TurnstileField from 'components/form/TurnstileField';
import { selectConfig } from 'selectors/configSelectors';
import useTranslate from 'components/hooks/useTranslate';
import FormLabel from 'components/form/FormLabel';
import RegistrationRedirect from './RegistrationRedirect';

const renunciationSchema = yup.object({
  lastName: yup.string().nullable().required(),
  firstName: yup.string().nullable().required(),
  signatureDate: yup.date().nullable().required(),
  program: yup.string().nullable().required(),
  email: yup.string().email().nullable().required(),
  agreement: yup.boolean().nullable().oneOf([true]),
  turnstileToken: yup.string().required(),
});

type RenunciationSchema = yup.InferType<typeof renunciationSchema>;

function RegistrationRenunciationPublicForm(): JSX.Element {
  const t = useTranslate();
  const config = useSelector(selectConfig);
  const locale = useLocale();
  const messages = useMessages(
    (t, e) => ({
      title: t('registration.renunciation.form.title'),
      description1: t('registration.renunciation.form.description1'),
      lastNameLabel: t('registration.renunciation.form.lastNameLabel'),
      firstNameLabel: t('registration.renunciation.form.firstNameLabel'),
      signatureDateLabel: t('registration.renunciation.form.signatureDateLabel'),
      programPlaceholder: t('registration.renunciation.form.programPlaceholder'),
      programLabel: t('registration.renunciation.form.programLabel'),
      emailLabel: t('registration.renunciation.form.emailLabel'),
      agreementLabel: t('registration.renunciation.form.agreementLabel'),
      legalNotice1Label: t('registration.renunciation.form.legalNotice1Label'),
      legalNotice2Label: t('registration.renunciation.form.legalNotice2Label'),
      submit: t('registration.renunciation.form.submitLabel'),
      submitTitle: t('registration.renunciation.submitted.title'),
      submitDescription1: t('registration.renunciation.submitted.description1'),
      submitDescription2: (
        date: ParsableDate,
        preset?: DateFormatPreset,
        format?: string,
        setZone?: boolean,
      ) =>
        t('registration.renunciation.renounced.description2', {
          date: dateFormat(date, preset, format, { setZone }),
        }),
      loading: t('label.loading'),
      e,
    }),
    [],
  );

  const [registrationRenunciation] = useState<RegistrationRenunciationModel | undefined>();

  const superviewer = useSelector(selectSuperviewer);
  const [renouncedRegistration, renunciationLoading, signatureError, handleRenunciation] =
    useCdmApi(
      `/candidate/registration/renunciation?locale=${locale}`,
      isRegistrationRenunciationModel,
      {
        params: superviewer,
      },
      false,
    );

  const { externalProgramOptions = null } = useData(['externalProgramOptions'], {
    locale,
  });

  const initialValues = useMemo((): RenunciationSchema => {
    const initialValues: RenunciationSchema = {
      lastName: registrationRenunciation?.lastName ?? '',
      firstName: registrationRenunciation?.firstName ?? '',
      signatureDate: null,
      program: '',
      email: registrationRenunciation?.email ?? '',
      agreement: false,
      turnstileToken: '',
    };

    return initialValues;
  }, [
    registrationRenunciation?.email,
    registrationRenunciation?.firstName,
    registrationRenunciation?.lastName,
  ]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: renunciationSchema,
    onSubmit: useCallback(
      (values: RenunciationSchema) => {
        const signatureDate = values.signatureDate
          ? dateParse(values.signatureDate).toString()
          : null;

        handleRenunciation({
          method: 'POST',
          data: {
            ...values,
            signatureDate,
            // parents,
          },
        });
      },
      [handleRenunciation],
    ),
  });

  const handleTurnstileTokenChange = useCallback(
    (token: string) => {
      formik.setFieldValue('turnstileToken', token);
    },
    [formik],
  );

  if (renouncedRegistration) {
    return (
      <RegistrationRedirect title={messages.submitTitle}>
        <p>{messages.submitDescription1}</p>
        <p>{messages.submitDescription2(new Date(), null as any, 'dd/MM/yyyy')}</p>
      </RegistrationRedirect>
    );
  }

  return (
    <Flex direction="column">
      <Title as="h2" size={2}>
        {messages.title}
      </Title>
      <Editorial>
        <p>{messages.description1}</p>
      </Editorial>
      {/* Show loading icon when loading program list*/}
      {/* Only show the form only if having external program*/}
      {externalProgramOptions && !renunciationLoading && externalProgramOptions !== null ? (
        externalProgramOptions.length > 0 ? (
          renderForm()
        ) : (
          <Editorial>
            <p>{t('error.default')}</p>
          </Editorial>
        )
      ) : (
        <Editorial>
          <Spinner aria-label={messages.loading} />
        </Editorial>
      )}
    </Flex>
  );

  function renderForm(): JSX.Element | null {
    return (
      <FormikProvider value={formik}>
        <Form>
          {signatureError ? <FormError>{messages.e(signatureError)}</FormError> : null}
          <Flex direction="column" gap="md" gutter>
            <FormFieldset outline>
              <Flex gap="sm" gutter>
                <FormField label={messages.firstNameLabel} labelProps={{ fontWeight: 'normal' }}>
                  <TextInput name="firstName" required />
                </FormField>
                <FormField label={messages.lastNameLabel} labelProps={{ fontWeight: 'normal' }}>
                  <TextInput name="lastName" required />
                </FormField>
              </Flex>
              <Flex gap="sm" gutter>
                <FormField
                  label={messages.signatureDateLabel}
                  labelProps={{ fontWeight: 'normal' }}
                >
                  <DateTimeInput name="signatureDate" type="date" required />
                </FormField>
              </Flex>
              <Flex gap="sm">
                <FormField label={messages.programLabel} labelProps={{ fontWeight: 'normal' }}>
                  <SelectInput
                    name="program"
                    placeholder={messages.programPlaceholder}
                    options={externalProgramOptions !== null ? externalProgramOptions : []}
                    required
                  />
                </FormField>
              </Flex>
              <Flex gap="sm">
                <FormField
                  label={messages.emailLabel}
                  labelProps={{ fontWeight: 'normal', hidden: true }}
                >
                  <FormLabel
                    label={messages.emailLabel + ' * (1)(2)'}
                    style={{ fontWeight: 'normal' }}
                  ></FormLabel>
                  <TextInput name="email" required />
                </FormField>
              </Flex>
            </FormFieldset>
            <FormFieldset>
              <Flex gap="sm">
                <FormField labelProps={{ fontWeight: 'normal', hidden: true }}>
                  <CheckboxField name="agreement" label={messages.agreementLabel} />
                </FormField>
              </Flex>
            </FormFieldset>
          </Flex>
          <FormFooter style={{ marginTop: '1em', marginBottom: '1em' }}>
            <Flex inline alignItems="center" gap="xs" gutter>
              <Button
                type="submit"
                disabled={renunciationLoading || !(formik.isValid && formik.dirty)}
                kind="secondary"
                icon={ChevronRight}
              >
                {messages.submit}
              </Button>
              {renunciationLoading ? (
                <Spinner aria-label={messages.loading} kind="secondary" />
              ) : null}
            </Flex>
          </FormFooter>
          {signatureError ? <FormError>{messages.e(signatureError)}</FormError> : null}
          <FormikViolations violations={signatureError?.violations}>
            {(violations) =>
              violations.map((violation) => <FormError>{violation.message}</FormError>)
            }
          </FormikViolations>
          <Editorial>
            <p>{messages.legalNotice1Label}</p>
            <p>{messages.legalNotice2Label}</p>
          </Editorial>
          <TurnstileField
            siteKey={config.turnstileSiteKey ?? ''}
            onValueChange={(token) => handleTurnstileTokenChange(token)}
          />
        </Form>
      </FormikProvider>
    );
  }
}

export default RegistrationRenunciationPublicForm;
